import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ArrowRight, Info, LayoutDashboard, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAgents } from '../hooks/useAgents';
import { useAuth } from '../hooks/useAuth';
import { useAgentInteractions } from '../hooks/useAgentInteractions';
import { Agent } from '../types';
import MyAgentsSection from './MyAgentsSection';
import HomeActivityRow from './HomeActivityRow';
import type { ConversationSummary } from '../services/conversationHistoryService';
import PatchNotesModal from './PatchNotesModal';
import { APP_VERSION, getLatestVersion, ENABLE_VERSION_TRACKING } from '../config/patchNotesData';
import { SCOUT_CONTAINER } from './scoutLayout';
import { trackHomeEvent } from '../services/homeTelemetry';
import './LandingPage.css';

interface LandingPageProps {
  onQuestionSubmit: (question: string, agent?: string) => void;
  recentConversations?: ConversationSummary[];
  onOpenConversation?: (conversationId: string) => void;
}

type MentionTrigger = '@' | '/';

interface MentionState {
  start: number;
  end: number;
  trigger: MentionTrigger;
  query: string;
}

// Hero Section Component
const HeroSection = ({ 
  onQuestionSubmit,
  onVersionClick,
  hasUpdates,
  hasAdminAccess,
  onAdminClick
}: { 
  onQuestionSubmit: (question: string, agent?: string) => void;
  onVersionClick: () => void;
  hasUpdates: boolean;
  hasAdminAccess: boolean;
  onAdminClick: () => void;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [forcedAgent, setForcedAgent] = useState<Agent | null>(null);
  const [mentionState, setMentionState] = useState<MentionState | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const agentButtonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const { agents } = useAgents();

  const filteredAgents = useMemo(() => {
    // Filter out hidden agents (like Aldo routing agent) from the agent list
    const visibleAgents = agents.filter(agent => 
      !agent.hidden
    );
    
    if (!mentionState) {
      return visibleAgents;
    }
    const query = mentionState.query.toLowerCase();
    
    // Filter and sort: prioritize name/id matches over description matches
    const matches = visibleAgents
      .filter(agent => 
        agent.name.toLowerCase().includes(query) ||
        agent.id.toLowerCase().includes(query) ||
        (agent.description || '').toLowerCase().includes(query)
      )
      .sort((a, b) => {
        const aNameMatch = a.name.toLowerCase().includes(query);
        const aIdMatch = a.id.toLowerCase().includes(query);
        const bNameMatch = b.name.toLowerCase().includes(query);
        const bIdMatch = b.id.toLowerCase().includes(query);
        
        // Prioritize name/id matches over description matches
        const aScore = (aNameMatch || aIdMatch) ? 1 : 0;
        const bScore = (bNameMatch || bIdMatch) ? 1 : 0;
        
        if (aScore !== bScore) {
          return bScore - aScore; // Higher score first
        }
        
        // If both match name or both match description, sort alphabetically
        return a.name.localeCompare(b.name);
      });
    
    return matches;
  }, [agents, mentionState]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [mentionState?.query]);

  // Scroll highlighted item into view with next item partially visible
  useEffect(() => {
    if (mentionState && agentButtonRefs.current[highlightIndex]) {
      const element = agentButtonRefs.current[highlightIndex];
      const container = element?.parentElement;
      
      if (element && container) {
        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const itemHeight = elementRect.height;
        
        // Calculate scroll position to show ~40% of next item
        const desiredTop = element.offsetTop - (containerRect.height - itemHeight * 1.4);
        
        container.scrollTo({
          top: Math.max(0, desiredTop),
          behavior: 'smooth'
        });
      }
    }
  }, [highlightIndex, mentionState]);

  const detectMention = (value: string, cursor: number): MentionState | null => {
    for (let i = cursor - 1; i >= 0; i--) {
      const char = value[i];
      if (char === '@' || char === '/') {
        const prevChar = i === 0 ? ' ' : value[i - 1];
        if (i === 0 || /\s/.test(prevChar)) {
          const query = value.slice(i + 1, cursor);
          if (!query.includes(' ') && !query.includes('\n')) {
            return { start: i, end: cursor, trigger: char as MentionTrigger, query };
          }
        }
        break;
      }
    }
    return null;
  };

  const applyForcedAgent = (agent: Agent) => {
    if (mentionState) {
      const before = searchQuery.slice(0, mentionState.start);
      const after = searchQuery.slice(mentionState.end);
      setSearchQuery(`${before}${after}`.replace(/\s{2,}/g, ' ').trimStart());
    }
    setForcedAgent(agent);
    setMentionState(null);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackHomeEvent({ name: 'HomeSearchSubmitted', properties: {
        routing: forcedAgent ? 'mention' : 'scout',
        agentId: forcedAgent?.id ?? 'aldo',
      } });
      onQuestionSubmit(searchQuery.trim(), forcedAgent?.id);
      setSearchQuery('');
      setForcedAgent(null);
      setMentionState(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    const cursor = e.target.selectionStart || value.length;
    const mention = detectMention(value, cursor);
    setMentionState(mention);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (mentionState && filteredAgents.length > 0) {
      // Tab and ArrowDown both move down the list
      if (e.key === 'Tab' || e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev + 1) % filteredAgents.length);
        return;
      }
      // ArrowUp moves up the list
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => (prev - 1 + filteredAgents.length) % filteredAgents.length);
        return;
      }
      // Only Enter/Return selects the highlighted agent
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        const selected = filteredAgents[highlightIndex] || filteredAgents[0];
        if (selected) {
          applyForcedAgent(selected);
        }
        return;
      }
      // Escape closes the mention menu
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionState(null);
        return;
      }
    }

    // Normal Enter submits the form (when not in mention mode)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearchSubmit(e);
    }
  };

  // Suggestions are real sample questions from featured agents, each sent to the agent it belongs to,
  // so a chip is a working shortcut rather than a decorative prompt.
  const suggestions = useMemo(() => agents
    .filter(agent => agent.featured && !agent.hidden && agent.sampleQuestions?.length)
    .slice(0, 3)
    .map(agent => ({ agentId: agent.id, agentName: agent.name, question: agent.sampleQuestions![0] })),
  [agents]);

  return (
    <header className="relative">
      {/* A faint green wash in the corner ties the header to the brand without tinting the page. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(ellipse_at_top_right,rgba(0,177,148,0.08),transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top_right,rgba(0,177,148,0.05),transparent_55%)]"
      />

      <div className={`relative ${SCOUT_CONTAINER} pt-8 pb-6`}>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              {/* The PNG is square but the mark only fills its middle 504x324, so crop the empty
                  bands (object-cover at the mark's aspect) to sit it flush against the title. */}
              <img
                src="/assets/logos/aldo-no-back.png"
                alt=""
                className="w-[52px] h-[34px] sm:w-[64px] sm:h-[41px] object-cover shrink-0"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-scout-ink dark:text-white">
                Scout Central
              </h1>
            </div>
            <p className="mt-1.5 text-[13px] sm:text-sm text-scout-ink-2 dark:text-gray-300">
              Your AI workspace for UniFirst knowledge, policies, systems and operations.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {hasAdminAccess && (
              <button
                onClick={onAdminClick}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-brand-700 dark:text-brand-300 bg-white/80 dark:bg-scout-surface-dark border border-scout-line dark:border-scout-line-dark hover:border-brand-500 transition-colors"
                aria-label="Admin Dashboard"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span>Admin</span>
              </button>
            )}
            <button
              onClick={onVersionClick}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-scout-ink-2 dark:text-gray-300 bg-white/80 dark:bg-scout-surface-dark border border-scout-line dark:border-scout-line-dark hover:border-brand-500 hover:text-brand-700 transition-colors"
              aria-label="View patch notes"
            >
              <Info className="w-3 h-3" />
              <span className="tabular-nums">{APP_VERSION}</span>
              {hasUpdates && <span className="w-2 h-2 bg-brand-500 rounded-full" aria-label="New updates" />}
            </button>
          </div>
        </div>

        {/* Search panel: the primary interaction surface, so it is the one lifted element on the page. */}
        <form
          onSubmit={handleSearchSubmit}
          className="relative mt-7 rounded-2xl bg-scout-surface dark:bg-scout-surface-dark border border-gray-400 dark:border-gray-500 shadow-scout-panel focus-within:border-brand-600 dark:focus-within:border-brand-400 transition-colors"
        >
          <div className="flex items-center gap-3 p-3 sm:p-4">
            <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-scout-canvas dark:bg-scout-canvas-dark text-scout-ink-3 shrink-0">
              <Search className="w-5 h-5" aria-hidden />
            </span>
            <div className="relative flex-1 min-w-0">
              {forcedAgent && (
                <div className="flex items-center gap-2 mb-1 text-xs text-brand-700 dark:text-brand-300">
                  <span className="font-semibold">Routing to {forcedAgent.name}</span>
                  <button
                    type="button"
                    onClick={() => setForcedAgent(null)}
                    className="px-1.5 rounded-full bg-brand-50 dark:bg-brand-900/40 hover:bg-brand-100"
                    aria-label="Stop routing to this agent"
                  >
                    ×
                  </button>
                </div>
              )}
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask Scout a question, or type @ to choose an agent…"
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent border-none outline-none text-base sm:text-lg text-scout-ink dark:text-white placeholder:text-scout-ink-3"
                aria-label="Ask Scout a question"
              />
            </div>
            <button
              type="submit"
              className="flex items-center gap-1.5 px-4 sm:px-5 py-2.5 rounded-xl bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold shadow-sm transition-colors disabled:opacity-85 disabled:cursor-not-allowed shrink-0"
              disabled={!searchQuery.trim()}
            >
              <span className="hidden sm:inline">Ask Scout</span>
              <span className="sm:hidden">Ask</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {suggestions.length > 0 && (
            // Always one line: the chips share the row and truncate rather than wrap. Narrow screens
            // show fewer of them instead of squeezing three into slivers.
            <div className="flex flex-nowrap gap-2 px-3 sm:px-4 pb-3 sm:pb-4 -mt-1 overflow-hidden">
              {suggestions.map((suggestion, index) => (
                <button
                  type="button"
                  key={suggestion.agentId}
                  onClick={() => {
                    trackHomeEvent({ name: 'HomeSuggestionClicked', properties: { agentId: suggestion.agentId, position: index + 1 } });
                    onQuestionSubmit(suggestion.question, suggestion.agentId);
                  }}
                  title={`${suggestion.question} (asks ${suggestion.agentName})`}
                  className={`${index === 1 ? 'hidden sm:block' : index === 2 ? 'hidden lg:block' : 'block'} min-w-0 max-w-[20rem] shrink truncate px-3 py-1.5 rounded-full text-xs text-scout-ink-2 dark:text-gray-300 bg-scout-canvas dark:bg-scout-canvas-dark border border-scout-line dark:border-scout-line-dark hover:border-brand-500 hover:text-brand-700 dark:hover:text-brand-300 transition-colors`}
                >
                  {suggestion.question}
                </button>
              ))}
            </div>
          )}

          {mentionState && filteredAgents.length > 0 && (
            <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-scout-surface-dark border border-scout-line dark:border-scout-line-dark rounded-2xl shadow-scout-panel z-20 max-h-[260px] overflow-y-auto">
              {filteredAgents.map((agent, index) => (
                <button
                  type="button"
                  key={agent.id}
                  ref={(el) => { agentButtonRefs.current[index] = el; }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyForcedAgent(agent);
                  }}
                  className={`w-full text-left px-4 py-2 first:rounded-t-2xl last:rounded-b-2xl transition-colors ${
                    index === highlightIndex ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300' : 'text-scout-ink dark:text-gray-200 hover:bg-scout-canvas dark:hover:bg-scout-canvas-dark'
                  }`}
                >
                  <div className="text-sm font-medium">{agent.name}</div>
                  <div className="text-xs text-scout-ink-3 truncate">{agent.description}</div>
                </button>
              ))}
            </div>
          )}
        </form>
      </div>
    </header>
  );
};

// Main Landing Page Component
const LandingPage = React.memo<LandingPageProps>(({ onQuestionSubmit, recentConversations, onOpenConversation }) => {
  const navigate = useNavigate();
  const { hasAdminAccess } = useAuth();
  const [isPatchNotesOpen, setIsPatchNotesOpen] = useState(false);
  
  // Get accessible agents from useAgents hook
  const { agents, reloadAgents } = useAgents();
  
  // Get agent interactions to check for platform updates (only if tracking enabled)
  const { getLastSeenVersion } = useAgentInteractions();
  
  // Check if there are unseen platform updates (disabled when ENABLE_VERSION_TRACKING is false)
  const hasUpdates = useMemo(() => {
    if (!ENABLE_VERSION_TRACKING) return false;
    const platformLatestVersion = getLatestVersion('platform');
    const platformLastSeen = getLastSeenVersion('platform');
    return !platformLastSeen || platformLastSeen !== platformLatestVersion;
  }, [getLastSeenVersion]);
  
  // Handler for starting a conversation with a specific agent
  const handleStartConversation = (agentId: string) => {
    // Submit an empty or initial question to start the conversation
    // The agent's welcome message will be shown
    onQuestionSubmit('', agentId);
  };
  

  return (
    <div className="min-h-screen">
      <HeroSection 
        onQuestionSubmit={onQuestionSubmit} 
        onVersionClick={() => setIsPatchNotesOpen(true)}
        hasUpdates={hasUpdates}
        hasAdminAccess={hasAdminAccess()}
        onAdminClick={() => navigate('/admin')}
      />
      
      {/* My Agents Section - replaces QuickQuestionsCarousel and CapabilitiesSection */}
      <MyAgentsSection
        agents={agents}
        onStartConversation={handleStartConversation}
        onNoticeSaved={reloadAgents}
      />

      <HomeActivityRow
        agents={agents}
        conversations={recentConversations}
        onOpenConversation={onOpenConversation}
        onStartConversation={handleStartConversation}
      />
      
      {/* Patch Notes Modal */}
      <PatchNotesModal
        isOpen={isPatchNotesOpen}
        onClose={() => setIsPatchNotesOpen(false)}
        currentVersion={APP_VERSION}
      />
    </div>
  );
});

export default LandingPage;
